import { useMemo, useState } from 'react';

import { useCounterMode } from '@/shared/session/profiles';
import {
  type ColumnsView,
  FIELD_LABELS,
  missingFields,
  ORIGIN_LABELS,
  TARIFF_FIELDS,
  type TariffField,
  type Version,
} from '@/shared/tariffs/types';
import { useColumnsPreview, useConfirmColumns } from '@/shared/tariffs/useTariffs';
import { AiNotice } from '@/shared/ui/AiNotice';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Select } from '@/shared/ui/Input';

type Mapping = Partial<Record<TariffField, number>>;

/** `0` → `A`, `27` → `AB`: how Excel names the column, so the person finds it in the file. */
function columnLetter(index: number): string {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    const rest = (n - 1) % 26;
    name = String.fromCharCode(65 + rest) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

/** How many rows the «Fila de los títulos» offers: the titles are near the top. */
const HEADER_ROWS_OFFERED = 40;

/**
 * «Qué columna es cada dato» (contract v10.48, ADR 0043 of the back). The platform
 * proposes (the template of the tariff, the model looking at the titles and twenty rows, or
 * the titles alone) and **a person confirms** before a single row is read. The sheet and
 * the row of the titles can be changed: the back answers with the titles and rows there.
 * One column is one piece of data: choosing a column for a field takes it off another.
 */
export function ColumnsStep({
  version,
  columns,
  onCancel,
  onRead,
}: {
  version: Version;
  columns: ColumnsView;
  /** Shown when the columns are being changed on a draft: back to what was read. */
  onCancel?: () => void;
  /** The reading was asked for: the screen follows the version again. */
  onRead?: () => void;
}) {
  const counter = useCounterMode();
  const confirm = useConfirmColumns(version.id);
  const original = { sheet: columns.sheet ?? '', headerRow: columns.header_row ?? 0 };
  const [place, setPlace] = useState(original);
  const moved = place.sheet !== original.sheet || place.headerRow !== original.headerRow;
  const preview = useColumnsPreview(version.id, moved ? place : null);
  const view: ColumnsView = moved && preview.data ? preview.data : columns;
  // While the titles of the new row are on their way, what is painted is the row before:
  // its columns cannot be confirmed for this one (the button would send the old row).
  const waiting = moved && (!preview.data || preview.isPlaceholderData);
  const startingMapping = useMemo<Mapping>(
    () => (moved ? (preview.data?.proposal ?? {}) : (version.mapping ?? columns.proposal ?? {})),
    [moved, preview.data, version.mapping, columns.proposal],
  );
  const [chosen, setChosen] = useState<{ key: string; mapping: Mapping } | null>(null);
  const placeKey = `${place.sheet}|${place.headerRow}`;
  const mapping = chosen?.key === placeKey ? chosen.mapping : startingMapping;
  const titles = view.titles ?? [];
  const missing = missingFields(mapping);
  const byColumn = new Map<number, TariffField>(
    Object.entries(mapping).map(([field, index]) => [index, field as TariffField]),
  );

  function pick(field: TariffField, value: string) {
    const next: Mapping = { ...mapping };
    if (value === '') {
      delete next[field];
    } else {
      const index = Number(value);
      for (const [other, used] of Object.entries(next)) {
        if (used === index) delete next[other as TariffField];
      }
      next[field] = index;
    }
    setChosen({ key: placeKey, mapping: next });
  }

  if (counter) {
    return (
      <Callout tone="info" title="Quita el modo mostrador para elegir las columnas">
        Las filas de muestra del fichero pueden llevar los precios de compra.
      </Callout>
    );
  }

  const rowsOffered = Math.min(view.rows_in_sheet ?? 0, HEADER_ROWS_OFFERED);
  const origin = moved ? 'titles' : (version.mapping_origin ?? columns.origin);

  return (
    <Card
      title="Qué columna es cada dato"
      description="Revísalas antes de leer la tarifa: se leen todas las filas con estas columnas."
    >
      <div className="flex flex-col gap-4">
        {columns.template_changed && !moved ? (
          <Callout tone="warn" compact>
            Las columnas de este fichero no son las de la última vez. Revísalas con cuidado.
          </Callout>
        ) : null}
        {origin && origin in ORIGIN_LABELS ? (
          <p className="text-ink-3 text-[13px]">
            {ORIGIN_LABELS[origin as keyof typeof ORIGIN_LABELS]}
            {!moved && columns.note ? ` ${columns.note}` : ''}
          </p>
        ) : null}
        {origin === 'model' && !moved ? <AiNotice /> : null}

        <div className="grid gap-3 sm:grid-cols-2">
          {(view.sheets ?? []).length > 1 ? (
            <Select
              label="Hoja"
              value={place.sheet}
              onChange={(event) => setPlace({ sheet: event.target.value, headerRow: 0 })}
            >
              {(view.sheets ?? []).map((sheet) => (
                <option key={sheet} value={sheet}>
                  {sheet}
                </option>
              ))}
            </Select>
          ) : null}
          <Select
            label="Fila de los títulos"
            value={String(place.headerRow)}
            onChange={(event) => setPlace({ ...place, headerRow: Number(event.target.value) })}
          >
            {Array.from({ length: Math.max(rowsOffered, place.headerRow + 1) }, (_, row) => (
              <option key={row} value={row}>
                Fila {row + 1}
              </option>
            ))}
          </Select>
        </div>

        {moved && preview.isError ? (
          <ErrorMessage error={preview.error}>No se pudo leer esa fila</ErrorMessage>
        ) : waiting ? (
          <p className="text-ink-3 text-[13px]" role="status">
            Leyendo los títulos de esa fila…
          </p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          {TARIFF_FIELDS.map((field) => (
            <Select
              key={field}
              label={FIELD_LABELS[field]}
              value={mapping[field] === undefined ? '' : String(mapping[field])}
              onChange={(event) => pick(field, event.target.value)}
            >
              <option value="">— No está en el fichero —</option>
              {titles.map((title, index) => (
                <option key={index} value={index}>
                  {columnLetter(index)} · {title || '(sin título)'}
                </option>
              ))}
            </Select>
          ))}
        </div>

        <div className={waiting ? 'opacity-50' : undefined} aria-busy={waiting}>
          <Sample view={view} byColumn={byColumn} />
        </div>

        {missing.length > 0 ? (
          <p className="text-ink-2 text-[13.5px]">Falta elegir {missing.join(' y ')}.</p>
        ) : null}
        {confirm.isError ? (
          <ErrorMessage error={confirm.error}>No se pudo pedir la lectura</ErrorMessage>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={missing.length > 0 || confirm.isPending || waiting}
            onClick={() =>
              confirm.mutate(
                {
                  sheet: view.sheet ?? place.sheet,
                  header_row: view.header_row ?? place.headerRow,
                  mapping,
                },
                { onSuccess: () => onRead?.() },
              )
            }
          >
            {confirm.isPending ? 'Pidiendo la lectura…' : 'Leer la tarifa con estas columnas'}
          </Button>
          {onCancel ? (
            <Button variant="secondary" onClick={onCancel} disabled={confirm.isPending}>
              Dejarlas como estaban
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/** The titles and twenty rows under them, each column named by what it was chosen for. */
function Sample({ view, byColumn }: { view: ColumnsView; byColumn: Map<number, TariffField> }) {
  const titles = view.titles ?? [];
  const rows = view.sample ?? [];
  if (titles.length === 0) return null;
  return (
    <div className="border-line max-w-full overflow-x-auto rounded-lg border">
      <table className="min-w-full text-[12.5px]" aria-label="Primeras filas del fichero">
        <thead className="bg-paper-2">
          <tr>
            {titles.map((title, index) => (
              <th key={index} className="text-ink-2 px-2 py-1.5 text-left align-bottom font-medium">
                <span className="text-ink-4 block font-normal">{columnLetter(index)}</span>
                {byColumn.has(index) ? (
                  <span className="text-accent block">
                    {FIELD_LABELS[byColumn.get(index) as TariffField]}
                  </span>
                ) : null}
                <span className="block whitespace-nowrap">{title || '—'}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-line border-t">
              {titles.map((_, index) => (
                <td
                  key={index}
                  className={
                    byColumn.has(index)
                      ? 'text-ink px-2 py-1 whitespace-nowrap'
                      : 'text-ink-4 px-2 py-1 whitespace-nowrap'
                  }
                >
                  {row[index] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
